import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Backdrop,
  Box,
  Container,
  CircularProgress,
  Grid,
} from '../../../components/mui/Core/index'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import InvoiceTable from './components/InvoiceTable/invoice-table'
import TableSearch from '../../../components/TableSearch/table-search'
import { fetchInvoices } from './invoice-slice'
import { fetchCreditInvoices } from '../CreditInvoices/credit-invoice-slice'
import CreditInvoiceSummaryTable from './components/CreditInvoiceSummaryTable/credit-invoice-summary-table'
import { openFileInBrowser } from '../../../utils/file-utils'
import { downloadInvoice } from '../../../api/invoice/download-invoice'
import { businessMessages } from '../../../constants/business-message-constants'
import { fileTypes } from '../../../constants/file-types'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import { hasUserPermission } from '../../../utils/user-utils'
import { userPermissionConstants } from '../../../constants/user-permission-constants'
import SelectClientDropdown from '../../../components/dropdowns/SelectClientDropdown/select-client-dropdown'
import ClientSelection from '../../../components/ClientSelection/client-selection'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'
import { INVOICE_PAYMENT_STATUS_OPTIONS } from '../../../constants/status'

function Invoices() {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const searchByOptions = [
    { id: 'invoiceNumber', text: 'Invoice #', type: 'string' },
    { id: 'invoiceDateIssued', text: 'Date Issued', type: 'date' },
    { id: 'invoiceDueDate', text: 'Date Due', type: 'date' },
    { id: 'invoiceType', text: 'Invoice Type', type: 'string' },
    { id: 'paymentStatus', text: 'Status', type: 'dropdown' },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const fetchInvoicesError = useSelector((state) => state.invoice.fetchInvoicesError)
  const fetchCreditInvoicesError = useSelector(
    (state) => state.creditInvoice.fetchCreditInvoicesError,
  )
  const [selectedClient, setSelectedClient] = useState(
    JSON.parse(sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT)),
  )
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })
  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.invoices.INVOICE_TITLE}`
    : Constants.invoices.INVOICE_TITLE
  useBrowserTabTitles(browserTabTitle)

  useEffect(() => {
    /* Dispatch actions to fetch all invoices */
    async function getAllInvoices() {
      setIsLoading(true)
      await Promise.all([dispatch(fetchInvoices()), dispatch(fetchCreditInvoices())])
    }
    if (!hasSwitchClientPermission || (hasSwitchClientPermission && selectedClient)) {
      getAllInvoices()
        .then(() => setIsLoading(false))
        .catch((exception) => {
          setIsLoading(false)
          dispatch(
            setSnackbar({
              message: JSON.stringify(exception),
              severity: 'error',
              action: true,
            }),
          )
        })
    }
  }, [dispatch, hasSwitchClientPermission, selectedClient])

  useEffect(() => {
    if (fetchInvoicesError) {
      dispatch(
        setSnackbar({
          message: fetchInvoicesError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }

    if (fetchCreditInvoicesError) {
      dispatch(
        setSnackbar({
          message: fetchCreditInvoicesError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
  }, [fetchInvoicesError, fetchCreditInvoicesError, dispatch])

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const handleSelectClientDropdown = (client) => {
    setSelectedClient(client)
    returnSearch('', 0) // Reset the table filter
  }

  const handleDisplayInvoice = async (invoiceId) => {
    const requestData = { invoiceId }
    await downloadInvoice(requestData)
      .then((response) => {
        if (response?.data) {
          openFileInBrowser(response.data, fileTypes.download.PDF).catch(() => {
            dispatch(
              setSnackbar({
                message: businessMessages.fileDownload.FAILED,
                severity: 'error',
                action: true,
              }),
            )
          })
        } else {
          dispatch(
            setSnackbar({
              message: businessMessages.fileDownload.FAILED,
              severity: 'error',
              action: true,
            }),
          )
        }
      })
      .catch(() => {
        dispatch(
          setSnackbar({
            message: businessMessages.fileDownload.FAILED,
            severity: 'error',
            action: true,
          }),
        )
      })
  }

  const tableSearchWrapperStyle = {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
  }

  const sideColumnStyle = {
    paddingTop: '0px !important',
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <GenericNav selected={Constants.genericNav.navLinks.INVOICES} user='authenticated' />
      <Container maxWidth='xl'>
        <Grid container spacing={3}>
          <Grid size={{ xs: hasSwitchClientPermission ? 12 : 4 }}>
            <PageTitle title={Constants.invoices.LOGISTICS_INVOICES} />
          </Grid>
          {hasSwitchClientPermission &&
            (!selectedClient ? (
              <Grid size={{ xs: 6 }}>
                <ClientSelection
                  handleSelectOption={handleSelectClientDropdown}
                  message={Constants.invoices.CLIENT_SELECTION_MESSAGE}
                />
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 6 }}>
                  <SelectClientDropdown
                    currentState={selectedClient}
                    handleSelectOption={handleSelectClientDropdown}
                  />
                </Grid>
                <Grid size={{ xs: 3 }} sx={tableSearchWrapperStyle}>
                  <TableSearch
                    key={selectedClient?.clientCode || 'clientCode'}
                    returnSearch={returnSearch}
                    searchByOptions={searchByOptions}
                    statusOptions={INVOICE_PAYMENT_STATUS_OPTIONS}
                  />
                </Grid>
                <Grid size={{ xs: 8.5, lg: 9 }}>
                  <InvoiceTable
                    filterBy={tableFilterBy}
                    filterValue={tableFilterValue}
                    handleDisplayInvoice={handleDisplayInvoice}
                    searchByOptions={searchByOptions}
                  />
                </Grid>
                <Grid size={{ xs: 3.5, lg: 3 }} sx={sideColumnStyle}>
                  <CreditInvoiceSummaryTable />
                </Grid>
              </>
            ))}
          {!hasSwitchClientPermission && (
            <>
              <Grid size={{ xs: 6 }} sx={tableSearchWrapperStyle}>
                <TableSearch
                  returnSearch={returnSearch}
                  searchByOptions={searchByOptions}
                  statusOptions={INVOICE_PAYMENT_STATUS_OPTIONS}
                />
              </Grid>
              <Grid size={{ xs: 8 }}>
                <InvoiceTable
                  filterBy={tableFilterBy}
                  filterValue={tableFilterValue}
                  handleDisplayInvoice={handleDisplayInvoice}
                  searchByOptions={searchByOptions}
                />
              </Grid>
              <Grid size={{ xs: 4 }} sx={sideColumnStyle}>
                <CreditInvoiceSummaryTable />
              </Grid>
            </>
          )}
        </Grid>
      </Container>
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <GenericSnackbar />
    </Box>
  )
}

export default Invoices
